<script setup>
import {
  useRevendasMapaStore,
  estadosOptions,
  nomeCampoOptions,
  selecionaOptions,
  statusAtivoOptions,
  statusPedidoOptions,
  statusIconColorsPedidos,
  pedidosOrigens,
  produtoOptions,
} from '@/stores/revendasMapa'
import { ref, onMounted, onUnmounted } from 'vue'
import { loadGoogleMapsApi } from '@/plugins/googleMapsLoader'
import AddressAutocomplete from '@/components/inputs/AddressAutocomplete.vue'

const revendasMapaStore = useRevendasMapaStore()

const filtersExpanded = ref(0) // abre o primeiro painel por padrão
// Garante o objeto de filtros e alguns defaults da versão 1
if (!revendasMapaStore.filtros) revendasMapaStore.filtros = {}
if (revendasMapaStore.filtros.nome_campo_filtro === undefined) {
  revendasMapaStore.filtros.nome_campo_filtro = 'nomefantasia'
}

const loading = ref(false)

const loadingRequest = ref({
  revendas: false,
  pedidos: false,
  enderecos: false,
  salvar: false,
})

const selectedPolygonIndex = ref(null)
const isSaveEnabled = ref(false)
const modifiedPolygons = ref([])
const showSearchAddress = ref(false)

const mapLoading = ref(false)
const mapError = ref(null)
const poligonosLoading = ref(false)
const editMode = ref(false) // Add a ref to track if we're in polygon creation mode
const isDrawing = ref(false) // Add a ref to track if we're currently drawing a polygon

// novo contador reativo para permitir habilitar botão "Finalizar Desenho" na template
const drawingPointsCount = ref(0)

let map = null
let polygons = []
/* new: armazenar marcadores de pedidos para poder limpar depois */
let pedidoMarkers = []
/* NEW: armazenar marcadores de endereços para poder limpar depois */
let enderecoMarkers = []
let drawingPath = []
let tempPolygon = null
let tempMarkers = []
let mapClickListener = null // Add this variable at component level
const mapContainer = ref(null)
// const poligonos = computed(() => revendaStore.poligonos || []);

// Define map styles to hide POIs but keep streets and other geographic features
const mapStyles = {
  // Normal map style with everything visible
  withAllFeatures: [],

  // Editing mode: Hide POIs but keep streets and important geographic labels
  withoutPOIs: [
    {
      // Hide all points of interest
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
    {
      // Hide business points
      featureType: 'poi.business',
      stylers: [{ visibility: 'off' }],
    },
    {
      // Keep street labels visible
      featureType: 'road',
      elementType: 'labels',
      stylers: [{ visibility: 'on' }],
    },
    {
      // Keep neighborhood names
      featureType: 'administrative.neighborhood',
      elementType: 'labels',
      stylers: [{ visibility: 'on' }],
    },
  ],
}

// InfoWindow used for hover tooltip
let infoWindow = null

// helper: normalize any polygon-like input into array of rings (each ring => [{lat,lng}, ...])
const normalizeToRings = (poly) => {
  const rings = []

  // if string, try parse
  if (typeof poly === 'string') {
    try {
      poly = JSON.parse(poly)
    } catch {
      // not json, ignore
      return rings
    }
  }

  // GeoJSON Polygon
  if (
    poly &&
    typeof poly === 'object' &&
    poly.type === 'Polygon' &&
    Array.isArray(poly.coordinates)
  ) {
    const ring = poly.coordinates[0] || []
    const path = ring.map((coord) => {
      const a = parseFloat(coord[0]),
        b = parseFloat(coord[1])
      if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return { lat: a, lng: b }
      return { lat: b, lng: a }
    })
    rings.push(path)
    return rings
  }

  // GeoJSON MultiPolygon
  if (
    poly &&
    typeof poly === 'object' &&
    poly.type === 'MultiPolygon' &&
    Array.isArray(poly.coordinates)
  ) {
    poly.coordinates.forEach((mp) => {
      const ring = mp[0] || []
      const path = ring.map((coord) => {
        const a = parseFloat(coord[0]),
          b = parseFloat(coord[1])
        if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return { lat: a, lng: b }
        return { lat: b, lng: a }
      })
      rings.push(path)
    })
    return rings
  }

  // Already an array of rings: [ [ [lng,lat], ... ], ... ] OR nested
  if (Array.isArray(poly) && Array.isArray(poly[0])) {
    // determine nesting
    const maybeRing = Array.isArray(poly[0][0]) ? poly[0] : poly
    const ring = maybeRing[0] && Array.isArray(maybeRing[0][0]) ? maybeRing[0] : maybeRing
    const path = ring.map((coord) => {
      const a = parseFloat(coord[0]),
        b = parseFloat(coord[1])
      if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return { lat: a, lng: b }
      return { lat: b, lng: a }
    })
    rings.push(path)
    return rings
  }

  return rings
}

// Build and show polygons for current revendas
const buscarPoligonos = async (requestApi = true) => {
  // only run when map is ready
  if (!map) return

  loadingRequest.value = {
    ...loadingRequest.value,
    revendas: true,
  }

  try {
    if (requestApi) {
      // fetch data (this updates revendasMapaStore.revendas)
      await revendasMapaStore.searchRevendas()
    }

    // get revendas from store
    const revendas = revendasMapaStore.revendas || []

    // clear previous polygons and listeners
    try {
      polygons.forEach((pObj) => {
        if (pObj.polygon) {
          google.maps.event.clearInstanceListeners(pObj.polygon)
          pObj.polygon.setMap(null)
        }
      })
    } catch {
      // ignore
    }
    polygons = []

    // create one InfoWindow instance
    if (!infoWindow)
      infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })

    // draw polygons for each revenda
    const bounds = new google.maps.LatLngBounds()
    let anyCoord = false

    for (let rIndex = 0; rIndex < revendas.length; rIndex++) {
      const revenda = revendas[rIndex]
      const name = revenda.nomefantasia || '—'

      // normalize status values (booleans or numbers)
      const status_revenda = Number(revenda.status_revenda ?? 0)
      const status_revendedor = Number(revenda.status_revendedor ?? 0)
      const status_tmkt = Number(revenda.status_tmkt ?? 0)

      // campanha length safe check
      const campanhaLength = Array.isArray(revenda.campanha) ? revenda.campanha.length : 0

      // determine color according to provided rules
      let color = status_revenda === 0 || status_revendedor === 0 ? '#FFA500' : 'blue'
      color = status_tmkt === 0 && status_revenda !== 0 && status_revendedor !== 0 ? 'red' : color
      if (color === 'blue' && campanhaLength > 0) color = '#68ff03'

      // determine situacao text
      let sit = status_revenda === 0 || status_revendedor === 0 ? 'Inativa' : 'Ativa'
      sit =
        status_tmkt === 0 && status_revenda !== 0 && status_revendedor !== 0
          ? 'Desativada Tmkt'
          : sit

      // rawPolygons can be already normalized, or objects/string from API
      const rawPolygons = revenda.polygons || []

      // If revenda.polygons is a single GeoJSON object (not an array), ensure we iterate correctly
      const polyList = Array.isArray(rawPolygons) ? rawPolygons : [rawPolygons]

      for (let pIndex = 0; pIndex < polyList.length; pIndex++) {
        const poly = polyList[pIndex]
        const rings = normalizeToRings(poly)

        rings.forEach((pathCoords) => {
          if (!pathCoords || !pathCoords.length) return

          // extend bounds
          pathCoords.forEach((pt) => {
            if (typeof pt.lat === 'number' && typeof pt.lng === 'number') {
              bounds.extend({ lat: pt.lat, lng: pt.lng })
              anyCoord = true
            }
          })

          const polygon = new google.maps.Polygon({
            paths: pathCoords,
            strokeColor: color,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: color,
            fillOpacity: 0.25,
            map,
          })

          // mouseover to show info
          const overListener = polygon.addListener('mouseover', (e) => {
            const idLabel = revenda.idrevenda ?? revenda.id ?? '—'
            const content = `<div style="font-weight:medium;min-width:140px;color:black;"><strong style="font-size:16px;color:${color};">${name} - ${idLabel} <small>[${sit}]</small></strong></div>`
            infoWindow.setContent(content)

            if (e && e.latLng) infoWindow.setPosition(e.latLng)
            else {
              const b = new google.maps.LatLngBounds()
              pathCoords.forEach((pt) => b.extend(pt))
              infoWindow.setPosition(b.getCenter())
            }
            infoWindow.open(map)
          })

          const outListener = polygon.addListener('mouseout', () => {
            infoWindow.close()
          })

          polygons.push({ polygon, overListener, outListener, revendaIndex: rIndex })
        })
      }

      // also if revenda provides a centroid (lat/lon), include it in bounds
      if (revenda.lat && revenda.lon) {
        try {
          bounds.extend({ lat: parseFloat(revenda.lat), lng: parseFloat(revenda.lon) })
          anyCoord = true
        } catch (e) {
          // ignore
        }
      }
    }

    // fit map to bounds if we have coordinates
    if (anyCoord) {
      try {
        map.fitBounds(bounds)
      } catch {
        console.warn('Erro ao ajustar bounds do mapa')
      }
    }
  } catch {
    console.error('Erro ao buscar polígonos:')
  } finally {
    loadingRequest.value = {
      ...loadingRequest.value,
      revendas: false,
    }
  }
}

/* changed code: nova função buscarPedidos */
const buscarPedidos = async () => {
  // only run when map is ready
  if (!map) return

  loadingRequest.value = {
    ...loadingRequest.value,
    pedidos: true,
  }

  try {
    // call store to fetch pedidos (popula revendasMapaStore.pedidos)
    await revendasMapaStore.searchPedidos()

    const pedidos = revendasMapaStore.pedidos || []

    // clear previous pedido markers
    try {
      pedidoMarkers.forEach((mObj) => {
        if (mObj.over) google.maps.event.removeListener(mObj.over)
        if (mObj.out) google.maps.event.removeListener(mObj.out)
        if (mObj.marker) mObj.marker.setMap(null)
      })
    } catch {
      // ignore
    }
    pedidoMarkers = []

    // prepare InfoWindow
    if (!infoWindow)
      infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })

    const bounds = new google.maps.LatLngBounds()
    let anyCoord = false

    pedidos.forEach((p) => {
      const lat = parseFloat(p.lat_entrega)
      const lng = parseFloat(p.long_entrega)
      if (!isFinite(lat) || !isFinite(lng)) return

      anyCoord = true
      bounds.extend({ lat, lng })

      // find icon by status, fallback to first entry
      const iconObj =
        statusIconColorsPedidos.find((s) => String(s.value) === String(p.status)) ||
        statusIconColorsPedidos[0]
      const marker = new google.maps.Marker({
        position: { lat, lng },
        map,
        title: `Pedido ${p.idpedido}`,
        icon: {
          url: iconObj.link,
          // reduzir tamanho pela metade (de 50x82 para 25x41)
          scaledSize: new google.maps.Size(25, 41),
          // ajustar âncora para metade
          anchor: new google.maps.Point(12.5, 41),
          // origem (útil se for sprite)
          origin: new google.maps.Point(0, 0),
          // origem do label ajustada pela metade
          labelOrigin: new google.maps.Point(12.5, 9),
        },
      })

      const content = `<div style="min-width:140px"><strong>Pedido #${p.idpedido}</strong><br/>Origem: ${pedidosOrigens[p.origem].label}<br/>Produto: ${p?.categoria_produto === 'AGUA' ? 'Água' : 'Gás'}</div>`

      const over = marker.addListener('mouseover', (e) => {
        infoWindow.setContent(content)
        if (e && e.latLng) infoWindow.setPosition(e.latLng)
        else infoWindow.setPosition(marker.getPosition())
        infoWindow.open(map)
      })
      const out = marker.addListener('mouseout', () => {
        infoWindow.close()
      })

      pedidoMarkers.push({ marker, over, out })
    })

    if (anyCoord) {
      try {
        map.fitBounds(bounds)
      } catch {
        console.warn('Erro ao ajustar bounds para pedidos')
      }
    }
  } catch {
    console.error('Erro ao buscar pedidos:')
  } finally {
    loadingRequest.value = {
      ...loadingRequest.value,
      pedidos: false,
    }
  }
}

/* changed code: nova função buscarEnderecos */
const buscarEnderecos = async () => {
  if (!map) return

  loadingRequest.value = {
    ...loadingRequest.value,
    enderecos: true,
  }

  try {
    // chama o store (popula revendasMapaStore.enderecosBuscados)
    await revendasMapaStore.searchEnderecos()

    const results = revendasMapaStore.enderecosBuscados || []

    // limpa marcadores antigos de endereços
    try {
      enderecoMarkers.forEach((m) => {
        if (m.over) google.maps.event.removeListener(m.over)
        if (m.out) google.maps.event.removeListener(m.out)
        if (m.marker) m.marker.setMap(null)
      })
    } catch {
      // ignore
    }
    enderecoMarkers = []

    if (!infoWindow)
      infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })

    const bounds = new google.maps.LatLngBounds()
    let anyCoord = false

    results.forEach((r) => {
      const lat = parseFloat(r.latitude)
      const lng = parseFloat(r.longitude)
      if (!isFinite(lat) || !isFinite(lng)) return

      anyCoord = true
      bounds.extend({ lat, lng })

      const iconUrl = r.iconUrl || null
      const marker = new google.maps.Marker({
        position: { lat, lng },
        map,
        title: r.rua ? `${r.rua}, ${r.numero || ''}` : `Endereco ${r.id}`,
        icon: iconUrl
          ? {
              url: iconUrl,
              scaledSize: new google.maps.Size(25, 41),
              anchor: new google.maps.Point(12.5, 41),
            }
          : undefined,
      })

      const origemLabel = r.origemLabel || (r.origem !== undefined ? String(r.origem) : '-')
      const encontrado = String(r.encontrado ?? '').toUpperCase()
      const content = `<div style="min-width:160px"><strong>${origemLabel}</strong><br/>${r.rua ? `${r.rua}, ${r.numero || ''}` : ''}<br/>Encontrado: ${encontrado}</div>`

      const over = marker.addListener('mouseover', (e) => {
        infoWindow.setContent(content)
        if (e && e.latLng) infoWindow.setPosition(e.latLng)
        else infoWindow.setPosition(marker.getPosition())
        infoWindow.open(map)
      })
      const out = marker.addListener('mouseout', () => {
        infoWindow.close()
      })

      enderecoMarkers.push({ marker, over, out })
    })

    if (anyCoord) {
      try {
        map.fitBounds(bounds)
      } catch {
        console.warn('Erro ao ajustar bounds para endereços')
      }
    }
  } catch {
    console.error('Erro ao buscar endereços:')
  } finally {
    loadingRequest.value = {
      ...loadingRequest.value,
      enderecos: false,
    }
  }
}

const initializeMap = async () => {
  mapLoading.value = true
  mapError.value = null

  try {
    await loadGoogleMapsApi()

    // Default center (São Paulo, Brasil)
    let center = { lat: -23.55052, lng: -46.633309 }

    // // If we have coordinates from the revenda, use them
    // if (revenda.value && revenda.value.lat && revenda.value.lon) {
    //   center = {
    //     lat: parseFloat(revenda.value.lat),
    //     lng: parseFloat(revenda.value.lon)
    //   };
    // }

    // // Create the map
    map = new google.maps.Map(mapContainer.value, {
      center,
      zoom: 13,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControl: true,
      fullscreenControl: true,
      streetViewControl: true,
      drawingControl: false, // We'll manage drawing with our own buttons
    })

    // Add the revenda marker to the map
    // addRevendaMarker();
  } catch {
    console.error('Error initializing map:')
    mapError.value = 'Erro ao carregar o mapa. Verifique sua conexão ou tente novamente mais tarde.'
  } finally {
    mapLoading.value = false
  }
}

const onAddressSelected = (place) => {
  if (place && place.latitude && place.longitude) {
    map.setCenter({ lat: place.latitude, lng: place.longitude })
  }
}

/* new: quando o usuário escolher uma revenda no autocomplete, construir objeto selectedRevenda no store */
const onSelectRevenda = (selectedId) => {
  try {
    if (!selectedId) {
      revendasMapaStore.selectedRevenda = null
      return
    }
    revendasMapaStore.selectedRevendaId = selectedId
    const rev = revendasMapaStore.revendas.find((r) => Number(r.id) === Number(selectedId))
    if (!rev) return

    if (!infoWindow)
      infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })

    // converter polígonos da revenda para coordsJson: array de rings (cada ring = [{lat,lng},...])
    const rawPolygons = rev.polygons || []
    const polyList = Array.isArray(rawPolygons) ? rawPolygons : [rawPolygons]
    const coordsJson = []

    // limpar áreas anteriores do mapa antes de desenhar as novas
    try {
      if (polygons && polygons.length) {
        polygons.forEach((pObj) => {
          if (pObj.polygon) {
            google.maps.event.clearInstanceListeners(pObj.polygon)
            pObj.polygon.setMap(null)
          }
        })
      }
    } catch (e) {
      // ignore
    }
    polygons = []

    const bounds = new google.maps.LatLngBounds()

    const name = rev.nomefantasia || '—'
    const ativo = !!rev.ativo
    const tmktAtivo = !!rev.status_tmkt
    const fillColor = !ativo ? '#FFA500' : !tmktAtivo ? 'red' : 'blue'
    const strokeColor = !ativo ? '#FFA500' : !tmktAtivo ? 'red' : 'blue'

    polyList.forEach((p) => {
      const rings = normalizeToRings(p)
      rings.forEach((pathCoords, rIndex) => {
        if (!pathCoords || !pathCoords.length) return

        // extend bounds
        pathCoords.forEach((pt) => {
          if (typeof pt.lat === 'number' && typeof pt.lng === 'number') {
            bounds.extend({ lat: pt.lat, lng: pt.lng })
          }
        })

        // se for o primeiro anel (index 0), centraliza e dá zoom no mapa usando a primeira coordenada
        if (rIndex === 0 && pathCoords[0] && map) {
          try {
            map.setCenter({ lat: pathCoords[0].lat, lng: pathCoords[0].lng })
            // ajusta o zoom para destacar a área (ajuste o valor conforme necessário)
            map.setZoom(12)
          } catch (e) {
            // ignore
          }
        }

        const polygon = new google.maps.Polygon({
          paths: pathCoords,
          strokeColor,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor,
          fillOpacity: 0.25,
          map,
        })

        // mouseover to show info
        const overListener = polygon.addListener('mouseover', (e) => {
          const content = `<div style="font-weight:medium;min-width:140px;color:black;"><strong style="font-size:16px;color:${strokeColor};">${name}</strong><p>Ativo: <span style="color:${ativo ? 'black' : '#FFA500'}">${ativo ? 'Sim' : 'Não'}</span></p><p>Status TMKT: <span style="color:${tmktAtivo ? 'black' : 'red'}">${tmktAtivo ? 'Sim' : 'Não'}</span></p></div>`
          infoWindow.setContent(content)

          if (e && e.latLng) infoWindow.setPosition(e.latLng)
          else {
            const b = new google.maps.LatLngBounds()
            pathCoords.forEach((pt) => b.extend(pt))
            infoWindow.setPosition(b.getCenter())
          }
          infoWindow.open(map)
        })

        const outListener = polygon.addListener('mouseout', () => {
          infoWindow.close()
        })

        polygons.push({ polygon, overListener, outListener, revendaIndex: rIndex })
      })
    })

    // monta objeto esperado
    revendasMapaStore.selectedRevenda = {
      idrevenda: rev.id,
      idrevendedor: rev.idrevendedor || null,
      coordsJson,
    }

    attachPolygonListeners()
  } catch {
    console.error('Erro ao selecionar revenda:')
  }
}

onMounted(() => {
  initializeMap()
})

onUnmounted(() => {
  // Reset map styles to normal and clean polygons/listeners
  if (map) {
    map.setOptions({ styles: [] })
  }
  try {
    if (infoWindow) {
      infoWindow.close()
      infoWindow = null
    }
    polygons.forEach((pObj) => {
      if (pObj.polygon) {
        google.maps.event.clearInstanceListeners(pObj.polygon)
        pObj.polygon.setMap(null)
      }
    })
    polygons = []

    // clear pedido markers
    if (pedidoMarkers && pedidoMarkers.length) {
      pedidoMarkers.forEach((mObj) => {
        try {
          if (mObj.over) google.maps.event.removeListener(mObj.over)
          if (mObj.out) google.maps.event.removeListener(mObj.out)
          if (mObj.marker) mObj.marker.setMap(null)
        } catch (e) {
          // ignore
        }
      })
      pedidoMarkers = []
    }

    // clear endereco markers
    if (enderecoMarkers && enderecoMarkers.length) {
      enderecoMarkers.forEach((mObj) => {
        try {
          if (mObj.over) google.maps.event.removeListener(mObj.over)
          if (mObj.out) google.maps.event.removeListener(mObj.out)
          if (mObj.marker) mObj.marker.setMap(null)
        } catch (e) {
          // ignore
        }
      })
      enderecoMarkers = []
    }
  } catch (e) {
    // ignore
  }
})

// helper para anexar clique/edição aos polígonos presentes no array "polygons"
const attachPolygonListeners = () => {
  polygons.forEach((pObj, idx) => {
    try {
      // remove listeners se já existirem
      if (pObj.clickListener) google.maps.event.removeListener(pObj.clickListener)
    } catch (e) {}
    // clique seleciona e ativa edição no polígono (sem criar nada)
    pObj.clickListener = pObj.polygon.addListener('click', () => {
      // marca o índice selecionado e habilita edição daquele polígono
      selectedPolygonIndex.value = idx
      enablePolygonEditingByIndex(idx)
    })
  })
}

// atualiza selectedRevenda.coordsJson a partir dos polígonos atuais no mapa
const updateSelectedRevendaCoords = () => {
  if (!revendasMapaStore.selectedRevenda) return
  const coordsJson = []
  polygons.forEach((pObj) => {
    try {
      const path = pObj.polygon.getPath()
      const ring = []
      for (let i = 0; i < path.getLength(); i++) {
        const pt = path.getAt(i)
        ring.push({ lat: pt.lat(), lng: pt.lng() })
      }
      // ignora anéis vazios
      if (ring.length) coordsJson.push(ring)
    } catch (e) {
      // ignore invalid polygons
    }
  })
  revendasMapaStore.selectedRevenda = {
    ...revendasMapaStore.selectedRevenda,
    coordsJson,
  }
  // marcar que há alterações locais
  modifiedPolygons.value = JSON.parse(JSON.stringify(coordsJson))
  isSaveEnabled.value = !!coordsJson.length
}

// função que ativa edição para o polígono de índice dado
const enablePolygonEditingByIndex = (index) => {
  if (!polygons[index]) return
  // desativa edição em todos
  polygons.forEach((p) => {
    try {
      p.polygon.setEditable(false)
      p.polygon.setOptions({ strokeWeight: 2 })
    } catch {}
  })

  const pObj = polygons[index]
  try {
    pObj.polygon.setEditable(true)
    pObj.polygon.setOptions({ strokeWeight: 3 })
  } catch (e) {}

  selectedPolygonIndex.value = index

  // anexa listeners de alteração de path para manter coords sincronizadas
  try {
    if (pObj.pathListeners) {
      pObj.pathListeners.forEach((l) => google.maps.event.removeListener(l))
    }
  } catch (e) {}
  const path = pObj.polygon.getPath()
  const setAt = google.maps.event.addListener(path, 'set_at', () => updateSelectedRevendaCoords())
  const insertAt = google.maps.event.addListener(path, 'insert_at', () =>
    updateSelectedRevendaCoords(),
  )
  const removeAt = google.maps.event.addListener(path, 'remove_at', () =>
    updateSelectedRevendaCoords(),
  )
  pObj.pathListeners = [setAt, insertAt, removeAt]

  // atualização inicial
  updateSelectedRevendaCoords()
  // habilita salvar
  isSaveEnabled.value = true
  // entra em modo de edição
  editMode.value = true
}

// botão "Editar Área" expõe a edição do polígono selecionado (se houver), ou instrui a selecionar um polígono
const editarArea = () => {
  if (selectedPolygonIndex.value !== null) {
    enablePolygonEditingByIndex(selectedPolygonIndex.value)
  } else if (polygons.length === 1) {
    // se houver apenas um polígono, edita ele
    enablePolygonEditingByIndex(0)
  } else {
    // instruir usuário a clicar no polígono que deseja editar
    // um InfoWindow simples pode ser mostrado ou console
    if (!infoWindow)
      infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })
    infoWindow.setContent('<div style="min-width:160px">Clique no polígono que deseja editar</div>')
    // posiciona no centro do mapa
    try {
      infoWindow.setPosition(map.getCenter())
    } catch (e) {}
    infoWindow.open(map)
    setTimeout(() => infoWindow.close(), 2500)
  }
}

// iniciar criação de novo polígono
const adicionarArea = () => {
  if (!map) return

  // novo: exigir revenda selecionada para adicionar área
  if (!revendasMapaStore.selectedRevendaId) {
    if (!infoWindow)
      infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })
    infoWindow.setContent('<div>Selecione uma revenda antes de adicionar uma área.</div>')
    try {
      infoWindow.setPosition(map.getCenter())
    } catch (e) {}
    infoWindow.open(map)
    setTimeout(() => infoWindow.close(), 2400)
    return
  }

  // limpa desenho anterior
  cancelDrawing()

  editMode.value = true
  isDrawing.value = true
  drawingPath = []
  drawingPointsCount.value = 0
  tempMarkers = []

  tempPolygon = new google.maps.Polygon({
    paths: [],
    strokeColor: '#1976D2',
    strokeOpacity: 0.9,
    strokeWeight: 3,
    fillColor: '#1976D2',
    fillOpacity: 0.25,
  })
  tempPolygon.setMap(map)

  // helper para mostrar mensagens rápidas
  const showTemporaryMessage = (html, duration = 2200) => {
    if (!infoWindow)
      infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })
    infoWindow.setContent(`<div>${html}</div>`)
    try {
      infoWindow.setPosition(map.getCenter())
    } catch (e) {}
    infoWindow.open(map)
    setTimeout(() => {
      try {
        infoWindow.close()
      } catch (e) {}
    }, duration)
  }

  // listener para adicionar pontos
  mapClickListener = map.addListener('click', (e) => {
    const pt = { lat: e.latLng.lat(), lng: e.latLng.lng() }
    drawingPath.push(pt)
    drawingPointsCount.value = drawingPath.length
    tempPolygon.setPath(drawingPath)

    // marcador temporário
    const mk = new google.maps.Marker({
      position: e.latLng,
      map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 6,
        fillColor: '#1976D2',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
      },
    })
    tempMarkers.push(mk)

    // quando atingir 3 pontos, indicar ao usuário que pode finalizar (botão ou clique direito)
    if (drawingPath.length === 3) {
      showTemporaryMessage(
        'Você pode finalizar o desenho agora: clique com o botão direito ou pressione "Finalizar Desenho".',
        3500,
      )
    }
  })

  // finalizar desenho com rightclick
  const rightClickListener = map.addListener('rightclick', () => {
    if (drawingPath.length >= 3) {
      finishDrawing()
    } else {
      // instruir usuário
      if (!infoWindow)
        infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })
      infoWindow.setContent(
        '<div>Adicione pelo menos 3 pontos antes de finalizar (clique com o botão direito).</div>',
      )
      try {
        infoWindow.setPosition(map.getCenter())
      } catch (e) {}
      infoWindow.open(map)
      setTimeout(() => infoWindow.close(), 2200)
    }
  })

  // guardamos referência para poder remover se necessário
  tempPolygon._rightClickListener = rightClickListener

  // mensagem rápida
  if (!infoWindow)
    infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })
  infoWindow.setContent(
    '<div>Desenhe clicando no mapa. Clique com o botão direito para finalizar.</div>',
  )
  try {
    infoWindow.setPosition(map.getCenter())
  } catch (e) {}
  infoWindow.open(map)
  setTimeout(() => infoWindow.close(), 2000)
}

// finaliza desenho temporário e transforma em polígono editável no mapa
const finishDrawing = () => {
  if (!tempPolygon || drawingPath.length < 3) {
    if (!infoWindow)
      infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })
    infoWindow.setContent('<div>É necessário pelo menos 3 pontos para formar uma área.</div>')
    try {
      infoWindow.setPosition(map.getCenter())
    } catch (e) {}
    infoWindow.open(map)
    setTimeout(() => infoWindow.close(), 2200)
    return
  }

  // fecha caminho (opcional)
  const first = drawingPath[0]
  const last = drawingPath[drawingPath.length - 1]
  if (first.lat !== last.lat || first.lng !== last.lng) drawingPath.push(first)

  // remove temp markers
  tempMarkers.forEach((m) => {
    try {
      m.setMap(null)
    } catch {}
  })
  tempMarkers = []

  // Remove temp listeners
  if (mapClickListener) {
    google.maps.event.removeListener(mapClickListener)
    mapClickListener = null
  }
  if (tempPolygon._rightClickListener) {
    google.maps.event.removeListener(tempPolygon._rightClickListener)
    delete tempPolygon._rightClickListener
  }

  // Se houver um polígono selecionado, substituir seu path em vez de criar novo
  if (selectedPolygonIndex.value !== null && polygons[selectedPolygonIndex.value]) {
    const pObj = polygons[selectedPolygonIndex.value]

    // remove listeners de path antigos
    try {
      if (pObj.pathListeners) pObj.pathListeners.forEach((l) => google.maps.event.removeListener(l))
    } catch {
      // ignore
    }

    try {
      // atualiza caminho do polígono existente
      // use setPath para um único anel
      pObj.polygon.setPath(drawingPath)
      pObj.polygon.setEditable(true)
      pObj.polygon.setOptions({ strokeWeight: 3 })
    } catch {
      console.error('Erro ao aplicar novo path ao polígono selecionado')
    }

    // re-anexa listeners de alteração de path para manter coords sincronizadas
    try {
      const path = pObj.polygon.getPath()
      const setAt = google.maps.event.addListener(path, 'set_at', () =>
        updateSelectedRevendaCoords(),
      )
      const insertAt = google.maps.event.addListener(path, 'insert_at', () =>
        updateSelectedRevendaCoords(),
      )
      const removeAt = google.maps.event.addListener(path, 'remove_at', () =>
        updateSelectedRevendaCoords(),
      )
      pObj.pathListeners = [setAt, insertAt, removeAt]
    } catch {
      // ignore
    }

    // atualização do selectedRevenda.coordsJson
    updateSelectedRevendaCoords()

    // cleanup tempPolygon
    try {
      tempPolygon.setMap(null)
    } catch {}
    tempPolygon = null
    drawingPath = []
    drawingPointsCount.value = 0
    isDrawing.value = false
    editMode.value = true
    isSaveEnabled.value = true

    // garante que listeners de clique/hover do polígono continuam anexados
    attachPolygonListeners()
    return
  }

  // Caso não haja polígono selecionado, cria novo polígono (comportamento antigo)
  const finalPolygon = new google.maps.Polygon({
    paths: drawingPath,
    strokeColor: '#1976D2',
    strokeOpacity: 0.9,
    strokeWeight: 2,
    fillColor: '#1976D2',
    fillOpacity: 0.25,
    editable: true,
  })
  finalPolygon.setMap(map)

  // listeners para interação
  const over = finalPolygon.addListener('mouseover', (e) => {
    if (!infoWindow)
      infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })
    infoWindow.setContent('<div style="min-width:140px">Novo polígono</div>')
    if (e && e.latLng) infoWindow.setPosition(e.latLng)
    infoWindow.open(map)
  })
  const out = finalPolygon.addListener('mouseout', () => {
    if (infoWindow) infoWindow.close()
  })
  const click = finalPolygon.addListener('click', () => {
    // seleciona e habilita edição
    const idx = polygons.length
    enablePolygonEditingByIndex(idx)
  })

  // listeners de path para atualização
  const path = finalPolygon.getPath()
  const setAt = google.maps.event.addListener(path, 'set_at', () => updateSelectedRevendaCoords())
  const insertAt = google.maps.event.addListener(path, 'insert_at', () =>
    updateSelectedRevendaCoords(),
  )
  const removeAt = google.maps.event.addListener(path, 'remove_at', () =>
    updateSelectedRevendaCoords(),
  )

  polygons.push({
    polygon: finalPolygon,
    overListener: over,
    outListener: out,
    clickListener: click,
    pathListeners: [setAt, insertAt, removeAt],
  })

  // atualizar selectedRevenda coords
  updateSelectedRevendaCoords()

  // cleanup tempPolygon
  try {
    tempPolygon.setMap(null)
  } catch {}
  tempPolygon = null
  drawingPath = []
  drawingPointsCount.value = 0
  isDrawing.value = false
  editMode.value = true
  selectedPolygonIndex.value = polygons.length - 1
  isSaveEnabled.value = true
}

// cancelar desenho temporário
const cancelDrawing = () => {
  if (mapClickListener) {
    try {
      google.maps.event.removeListener(mapClickListener)
    } catch {}
    mapClickListener = null
  }
  if (tempPolygon) {
    try {
      tempPolygon.setMap(null)
    } catch {}
    tempPolygon = null
  }
  tempMarkers.forEach((m) => {
    try {
      m.setMap(null)
    } catch {}
  })
  tempMarkers = []
  drawingPath = []
  drawingPointsCount.value = 0
  isDrawing.value = false
  editMode.value = false
  selectedPolygonIndex.value = null
}

// excluir polígono selecionado
const excluirArea = () => {
  if (selectedPolygonIndex.value === null) {
    // pedir seleção ao usuário
    if (!infoWindow)
      infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })
    infoWindow.setContent('<div>Selecione um polígono clicando sobre ele para excluir</div>')
    try {
      infoWindow.setPosition(map.getCenter())
    } catch (e) {}
    infoWindow.open(map)
    setTimeout(() => infoWindow.close(), 1800)
    return
  }

  const idx = selectedPolygonIndex.value
  const pObj = polygons[idx]
  if (!pObj) return

  // remove listeners e polígono
  try {
    if (pObj.overListener) google.maps.event.removeListener(pObj.overListener)
    if (pObj.outListener) google.maps.event.removeListener(pObj.outListener)
    if (pObj.clickListener) google.maps.event.removeListener(pObj.clickListener)
    if (pObj.pathListeners) pObj.pathListeners.forEach((l) => google.maps.event.removeListener(l))
  } catch (e) {}

  try {
    pObj.polygon.setMap(null)
  } catch (e) {}
  polygons.splice(idx, 1)

  // atualizar índices/listeners restantes
  attachPolygonListeners()

  // atualizar selectedRevenda coords e Estado
  updateSelectedRevendaCoords()
  selectedPolygonIndex.value = null
  isSaveEnabled.value = !!revendasMapaStore.selectedRevenda?.coordsJson?.length
}

// helper: mostra um aviso temporário (pode ser usado por outras ações)
const showTemporaryMessage = (html, duration = 2200) => {
  if (!infoWindow)
    infoWindow = new google.maps.InfoWindow({ pixelOffset: new google.maps.Size(20, -40) })
  infoWindow.setContent(`<div>${html}</div>`)
  try {
    infoWindow.setPosition(map.getCenter())
  } catch (e) {}
  infoWindow.open(map)
  setTimeout(() => {
    try {
      infoWindow.close()
    } catch (e) {}
  }, duration)
}

// tentativa de salvar: valida existência de revenda selecionada e número mínimo de pontos
const attemptSave = () => {
  if (!revendasMapaStore.selectedRevendaId) {
    showTemporaryMessage('Selecione uma revenda antes de salvar a área.')
    return
  }
  const coordsJson = revendasMapaStore.selectedRevenda?.coordsJson || []
  if (!coordsJson.length) {
    showTemporaryMessage('Não há áreas para salvar. Desenhe uma área antes de salvar.')
    return
  }
  // verificar se todos os anéis têm ao menos 3 pontos
  const invalidRing = coordsJson.find((r) => !Array.isArray(r) || r.length < 3)
  if (invalidRing) {
    showTemporaryMessage('Cada área precisa conter pelo menos 3 pontos antes de salvar.')
    return
  }

  // tudo ok: chama handleSave do store
  try {
    revendasMapaStore.handleSave()
  } catch {
    console.error('Erro ao executar handleSave:')
  }
}
</script>

<template>
  <div>
    <v-row>
      <v-col cols="12" class="d-flex align-center justify-space-between pt-0">
        <h1 class="text-h4 mb-2">Revendas no Mapa</h1>
      </v-col>
    </v-row>

    <v-expansion-panels v-model="filtersExpanded" class="mb-2">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <v-row no-gutters>
            <div class="d-flex align-center">
              <v-icon class="mr-2">mdi-filter</v-icon>
              <span class="text-subtitle-1">Filtros</span>
            </div>
          </v-row>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <v-row>
            <!-- Status do Pedido -->
            <v-col cols="12" md="3">
              <v-select
                id="filtro_status_do_pedido"
                name="filtro_status_do_pedido"
                v-model="revendasMapaStore.filtros.filtro_status_do_pedido"
                :items="statusPedidoOptions"
                item-title="title"
                item-value="value"
                label="Status do Pedido"
                density="compact"
                hide-details
                class="mb-2"
              />
            </v-col>

            <!-- Período -->
            <v-col cols="12" md="3">
              <v-text-field
                id="dtinicio"
                name="dtinicio"
                v-model="revendasMapaStore.filtros.dtinicio"
                label="Data início"
                type="date"
                density="compact"
                hide-details
                class="mb-2"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                id="dtfinal"
                name="dtfinal"
                v-model="revendasMapaStore.filtros.dtfinal"
                label="Data fim"
                type="date"
                density="compact"
                hide-details
                class="mb-2"
              />
            </v-col>

            <!-- Status do Revendedor / Revenda / TMKT -->
            <v-col cols="12" md="3">
              <v-select
                id="filtro_status_revendedor"
                name="filtro_status_revendedor"
                v-model="revendasMapaStore.filtros.filtro_status_revendedor"
                :items="statusAtivoOptions"
                item-title="title"
                item-value="value"
                label="Status do Revendedor"
                density="compact"
                hide-details
                class="mb-2"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                id="filtro_status_revenda"
                name="filtro_status_revenda"
                v-model="revendasMapaStore.filtros.filtro_status_revenda"
                :items="statusAtivoOptions"
                item-title="title"
                item-value="value"
                label="Status da Revenda"
                density="compact"
                hide-details
                class="mb-2"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                id="filtro_status_ativado"
                name="filtro_status_ativado"
                v-model="revendasMapaStore.filtros.filtro_status_ativado"
                :items="statusAtivoOptions"
                item-title="title"
                item-value="value"
                label="Ativo pelo TMKT"
                density="compact"
                hide-details
                class="mb-2"
              />
            </v-col>

            <!-- Estado -->
            <v-col cols="12" md="3">
              <v-select
                id="filtro_estado"
                name="filtro_estado"
                v-model="revendasMapaStore.filtros.filtro_estado"
                :items="[
                  { title: 'Limpar seleção', value: '', icon: 'mdi-close-circle-outline' },
                  ...estadosOptions,
                ]"
                item-title="title"
                item-value="value"
                item-icon="icon"
                label="Estado/UF"
                density="compact"
                hide-details
                prepend-inner-icon="mdi-map-marker"
                class="mb-2"
                clearable
                clear-icon="mdi-close-circle"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-autocomplete
                id="filtro_cidade"
                name="filtro_cidade"
                v-model="revendasMapaStore.filtros.filtro_cidade"
                :items="revendasMapaStore.citiesList"
                item-title="title"
                item-value="value"
                label="Cidade"
                density="compact"
                hide-details
                prepend-inner-icon="mdi-city"
                class="mb-2"
                clearable
                no-data-text="Nenhuma cidade encontrada"
                :filter="
                  (item, queryText, itemText) => {
                    if (!queryText) return true
                    return String(itemText).toLowerCase().includes(String(queryText).toLowerCase())
                  }
                "
              />
            </v-col>

            <!-- Pesquisar por / Pesquisa -->
            <v-col cols="12" md="3">
              <v-select
                id="nome_campo_filtro"
                name="nome_campo_filtro"
                v-model="revendasMapaStore.filtros.nome_campo_filtro"
                :items="nomeCampoOptions"
                item-title="title"
                item-value="value"
                label="Pesquisar por"
                density="compact"
                hide-details
                class="mb-2"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                id="pesquisar"
                name="pesquisar"
                v-model="revendasMapaStore.filtros.pesquisar"
                label="Pesquisa"
                density="compact"
                hide-details
                class="mb-2"
              />
            </v-col>

            <!-- Encontrado / Campanha -->
            <v-col cols="12" md="3">
              <v-select
                id="encontrado_filtro"
                name="encontrado_filtro"
                v-model="revendasMapaStore.filtros.encontrado_filtro"
                :items="selecionaOptions"
                item-title="title"
                item-value="value"
                label="Encontrado"
                density="compact"
                hide-details
                class="mb-2"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                id="campanha_filtro"
                name="campanha_filtro"
                v-model="revendasMapaStore.filtros.campanha_filtro"
                :items="selecionaOptions"
                item-title="title"
                item-value="value"
                label="Campanha"
                density="compact"
                hide-details
                class="mb-2"
              />
            </v-col>

            <!-- Checkboxes -->
            <v-col cols="12" md="8">
              <div class="d-flex ga-2 text-caption">
                <div>
                  <v-checkbox
                    id="aplicar_pedidos"
                    name="aplicar_pedidos"
                    v-model="revendasMapaStore.filtros.aplicar_pedidos"
                    :true-value="'1'"
                    :false-value="''"
                    label="Aplicar o filtro de pesquisa nos pedidos"
                    hide-details
                    density="compact"
                    color="primary"
                  />
                </div>
                <div>
                  <v-checkbox
                    id="aplicar_buscas_enderecos"
                    name="aplicar_buscas_enderecos"
                    v-model="revendasMapaStore.filtros.aplicar_buscas_enderecos"
                    :true-value="'1'"
                    :false-value="''"
                    label="Aplicar o filtro de pesquisa nas buscas de endereços"
                    hide-details
                    density="compact"
                    color="primary"
                  />
                </div>
                <div>
                  <v-checkbox
                    id="reset_marker"
                    name="reset_marker"
                    v-model="revendasMapaStore.filtros.reset_marker"
                    :true-value="'1'"
                    :false-value="''"
                    label="Não limpar ícones"
                    hide-details
                    density="compact"
                    color="primary"
                  />
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="4">
              <div class="ml-auto d-flex ga-2">
                <!-- select produtos água ou gás -->
                <v-select
                  id="filtro_produto"
                  name="filtro_produto"
                  v-model="revendasMapaStore.filtros.filtro_produto"
                  :items="produtoOptions"
                  item-title="title"
                  item-value="value"
                  label="Tipo Produto"
                  density="compact"
                  hide-details
                  class="mb-2"
                  clearable
                  clear-icon="mdi-close-circle"
                />

                <v-btn text color="primary" @click="revendasMapaStore.resetFiltros()">
                  <v-icon left>mdi-refresh</v-icon>
                  Limpar Filtros
                </v-btn>
              </div>
            </v-col>

            <!-- botões pesquisa -->
            <v-col cols="12" md="3">
              <v-btn
                :disabled="loadingRequest.revendas"
                color="primary"
                class="w-100"
                @click="buscarPoligonos()"
              >
                {{ loadingRequest.revendas ? 'Carregando...' : 'Revendas' }}
              </v-btn>
            </v-col>

            <v-col cols="12" md="3">
              <v-btn
                :disabled="loadingRequest.pedidos"
                color="success"
                class="w-100"
                @click="buscarPedidos()"
              >
                {{ loadingRequest.pedidos ? 'Carregando...' : 'Pedidos' }}
              </v-btn>
            </v-col>

            <v-col cols="12" md="3">
              <!-- Alterado para usar attemptSave() que valida pontos e seleção de revenda -->
              <v-btn
                :disabled="loadingRequest.salvar || !revendasMapaStore.selectedRevendaId"
                color="accent"
                class="w-100"
                :style="{
                  cursor: !revendasMapaStore.selectedRevendaId ? 'not-allowed' : 'pointer',
                  opacity: !revendasMapaStore.selectedRevendaId ? 0.5 : 1,
                }"
                @click="attemptSave()"
              >
                Salvar
              </v-btn>
            </v-col>

            <v-col cols="12" md="3">
              <v-btn
                color="warning"
                class="w-100"
                :disabled="loadingRequest.enderecos"
                @click="buscarEnderecos()"
              >
                {{ loadingRequest.enderecos ? 'Buscando...' : 'Buscas Endereços' }}
              </v-btn>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Informações  -->
    <div class="d-flex align-center ga-4 pl-2 w-100 py-2 text-sm">
      <span> Revendas: {{ revendasMapaStore.revendas.length }} </span>
      <span> Pedidos: {{ revendasMapaStore.pedidos.length }} </span>
      <span> Endereços buscados: {{ revendasMapaStore.enderecosBuscados.length }} </span>
      <span> Buscas sim: {{ revendasMapaStore.countBuscasSim }} </span>
      <span> Buscas não: {{ revendasMapaStore.countBuscasNao }} </span>
      <!-- Autocomplete para selecionar revenda (filtrar mapa) -->
      <div class="ml-auto" style="width: 300px">
        <v-autocomplete
          v-if="revendasMapaStore.revendas.length"
          v-model="revendasMapaStore.selectedRevendaId"
          :items="revendasMapaStore.revendas"
          item-title="nomefantasia"
          item-value="id"
          dense
          hide-details
          clearable
          placeholder="Selecionar revenda para editar"
          style="min-width: 240px"
          @update:modelValue="
            (val) => {
              onSelectRevenda(val)
              if (!val) buscarPoligonos(false)
            }
          "
        />
      </div>
    </div>

    <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 300px">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>

    <v-card class="map-card mb-4">
      <v-card-text class="map-card-content pa-0">
        <div v-if="mapError" class="text-center py-4">
          <v-icon color="error" size="large" class="mb-2">mdi-map-marker-alert</v-icon>
          <h3 class="text-h6 mb-2 text-error">{{ mapError }}</h3>
          <v-btn color="primary" @click="initializeMap">Tentar novamente</v-btn>
        </div>

        <div v-else class="map-container">
          <div ref="mapContainer" id="map-container" class="map-element"></div>

          <div
            v-if="loading || mapLoading || poligonosLoading"
            class="map-loading-overlay d-flex justify-center align-center"
          >
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          </div>

          <!-- Map Controls Overlay -->
          <div class="map-controls">
            <v-card class="pa-2">
              <div class="d-flex flex-column ga-2">
                <v-btn
                  color="success"
                  variant="elevated"
                  prepend-icon="mdi-magnify"
                  @click="showSearchAddress = !showSearchAddress"
                >
                  Pesquisar
                </v-btn>
                <v-btn variant="elevated" prepend-icon="mdi-plus" @click="adicionarArea">
                  Adicionar Área
                </v-btn>

                <!-- botão "Finalizar Desenho" visível enquanto estiver desenhando -->
                <v-btn
                  v-if="isDrawing"
                  variant="elevated"
                  color="primary"
                  prepend-icon="mdi-check"
                  :disabled="drawingPointsCount < 3"
                  @click="finishDrawing"
                >
                  Finalizar Desenho
                </v-btn>

                <v-btn variant="elevated" prepend-icon="mdi-pencil" @click="editarArea">
                  Editar Área
                </v-btn>
                <v-btn variant="elevated" prepend-icon="mdi-delete" @click="excluirArea">
                  Excluir Área
                </v-btn>
              </div>
            </v-card>
          </div>
          <!-- campo pesquisa -->
          <div class="map-input-search" v-if="showSearchAddress">
            <v-card class="pa-2">
              <AddressAutocomplete
                @place-selected="onAddressSelected"
                label="Endereço para busca"
                placeholder="Digite um endereço completo"
              />
            </v-card>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped>
.map-page {
  display: flex;
  flex-direction: column;
  height: auto;
}

.map-card {
  margin-bottom: 16px;
  height: calc(100vh - 250px);
  min-height: 500px;
}

.map-card-content {
  height: 100%;
  padding: 0;
}

.map-container {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
}

.map-element {
  width: 100%;
  height: 100%;
}

.map-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 10;
}

.map-info {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 300px;
  z-index: 5;
}

/* New control panel style */
.map-controls {
  position: absolute;
  top: 52px;
  left: 10px;
  z-index: 5;
}

.map-input-search {
  position: absolute;
  width: 300px;
  top: 54px;
  left: 212px;
  z-index: 5;
}

/* New style for the save button */
.save-button {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
}

.border-t {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.suggestions-card {
  position: absolute;
  z-index: 10;
}

/* 
.gm-style-iw-d {
  overflow: scroll;
  max-height: 300px;
  background-color: black !important;
} */
</style>
